// fitness-timer.js - reusable timing engine, generalised from the V5 Bronco
// timer (V6_DESIGN §5.6, V6_PLAN Phase 0b #3). Protocols are pure config
// (CONFIG.fitness.protocols): 'stopwatch' counts up until stopped (Bronco);
// 'countdown' counts down from durationMs and auto-finishes at zero with
// optional beeps (Baseline Template B - 40-Second Dribble - in Phase 2).
//
// The engine owns time, state and formatting ONLY. Persistence (run rows,
// recorded times), grids and wave handling stay with the consumer - waves are
// a data concept (keep times, restart clock), not a timing one.
//
// Timing is timestamp-based throughout: every read re-derives from the start
// timestamp, so a backgrounded tab or slept screen never drifts the clock.

export const FITNESS_TIMER = (() => {

  /** Format milliseconds as m:ss.t (tenths) - honest given tap-based timing. */
  function formatMs(ms) {
    if (ms == null || ms < 0 || !isFinite(ms)) ms = 0;
    const tenths = Math.floor(ms / 100);
    const t = tenths % 10;
    const s = Math.floor(tenths / 10) % 60;
    const m = Math.floor(tenths / 600);
    return `${m}:${String(s).padStart(2, '0')}.${t}`;
  }

  // Short WebAudio beep. Created lazily (needs a user gesture on iOS); any
  // failure is swallowed - a silent timer must still time.
  let _audioCtx = null;
  function _beep(durationMs, freq) {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      _audioCtx = _audioCtx || new Ctx();
      const osc  = _audioCtx.createOscillator();
      const gain = _audioCtx.createGain();
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(_audioCtx.destination);
      gain.gain.setValueAtTime(0.3, _audioCtx.currentTime);
      osc.start();
      osc.stop(_audioCtx.currentTime + durationMs / 1000);
    } catch {
      /* no audio available - timing is unaffected */
    }
  }

  /**
   * Create a timer instance for one protocol.
   *
   * opts:
   *   protocol  - a CONFIG.fitness.protocols entry ({ mode, durationMs, beepAtSecondsLeft, ... })
   *   tickMs    - UI refresh interval (CONFIG.fitness.tickMs)
   *   onTick(displayMs, state) - called every tick while the ticker runs
   *   onFinish() - countdown only: fired once when the clock hits zero
   *   shouldRun() - optional guard; returning false self-stops the ticker
   *                 (e.g. the user navigated away from the timer screen)
   *   now() - time source, defaults to Date.now (injectable for tests)
   */
  function create(opts) {
    const protocol = opts.protocol || { mode: 'stopwatch' };
    const tickMs   = opts.tickMs || 100;
    const now      = opts.now || Date.now;

    let _startMs  = null;
    let _stopMs   = null;
    let _ticker   = null;
    let _finished = false;     // onFinish fired (countdown)
    let _beeped   = new Set(); // beep marks already played this run

    function state() {
      if (_startMs == null) return 'idle';
      if (_stopMs != null) return 'finished';
      // A countdown past its duration is finished even before the next tick.
      if (protocol.mode === 'countdown' && now() - _startMs >= protocol.durationMs) return 'finished';
      return 'running';
    }

    /** Milliseconds since start (frozen once stopped; capped for countdowns). */
    function elapsedMs() {
      if (_startMs == null) return 0;
      const end = _stopMs != null ? _stopMs : now();
      let ms = end - _startMs;
      if (protocol.mode === 'countdown') ms = Math.min(ms, protocol.durationMs);
      return Math.max(0, ms);
    }

    /** What the clock should show: elapsed (stopwatch) or remaining (countdown). */
    function displayMs() {
      return protocol.mode === 'countdown'
        ? Math.max(0, protocol.durationMs - elapsedMs())
        : elapsedMs();
    }

    function start(startedAtMs) {
      _startMs  = startedAtMs != null ? startedAtMs : now();
      _stopMs   = null;
      _finished = false;
      _beeped   = new Set();
    }

    function stop(stoppedAtMs) {
      if (_startMs == null) return;
      _stopMs = stoppedAtMs != null ? stoppedAtMs : now();
      stopTicker();
    }

    /**
     * Adopt server-side timestamps (ISO strings or ms) - how the Bronco screen
     * resumes a run that another load already started or stopped.
     */
    function attach(startedAt, stoppedAt) {
      _startMs  = startedAt == null ? null : (typeof startedAt === 'number' ? startedAt : Date.parse(startedAt));
      _stopMs   = stoppedAt == null ? null : (typeof stoppedAt === 'number' ? stoppedAt : Date.parse(stoppedAt));
      _finished = _stopMs != null;
      _beeped   = new Set();
      if (state() !== 'running') stopTicker();
    }

    function reset() {
      _startMs  = null;
      _stopMs   = null;
      _finished = false;
      _beeped   = new Set();
      stopTicker();
    }

    function _tick() {
      if (opts.shouldRun && !opts.shouldRun()) { stopTicker(); return; }

      if (protocol.mode === 'countdown' && state() !== 'idle') {
        const remaining = displayMs();
        (protocol.beepAtSecondsLeft || []).forEach(mark => {
          if (remaining <= mark * 1000 && !_beeped.has(mark)) {
            _beeped.add(mark);
            _beep(mark === 0 ? 600 : 120, 880);
          }
        });
        // Auto-finish exactly at the protocol duration, not at tick time.
        if (remaining <= 0 && !_finished) {
          _finished = true;
          _stopMs   = _startMs + protocol.durationMs;
          stopTicker();
          if (opts.onTick) opts.onTick(displayMs(), state());
          if (opts.onFinish) opts.onFinish();
          return;
        }
      }

      if (opts.onTick) opts.onTick(displayMs(), state());
      if (state() !== 'running') stopTicker();
    }

    function startTicker() {
      stopTicker();
      _ticker = setInterval(_tick, tickMs);
    }
    function stopTicker() {
      if (_ticker) { clearInterval(_ticker); _ticker = null; }
    }

    return { state, elapsedMs, displayMs, start, stop, attach, reset, startTicker, stopTicker };
  }

  return { formatMs, create };

})();
